import { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import Header from './Header/Header';
import { globalLogout } from '../Utils/Logout';
import Footer from '../Components/Footer/Footer';
const NotificationsPage = () => {
	const [notifications, setNotifications] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		fetchNotifications();
	}, []);

	const fetchNotifications = async () => {
		try {
			setLoading(true);
			const token = Cookies.get("ecom_user_token");

			const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/notification/getNotification`, {
				headers: {
					'Authorization': `Bearer ${token}`
				}
			});

			if (response.data.success) {
				setNotifications(response.data.notifications || []);
			} else {
				setError('Failed to fetch notifications');
			}
		} catch (err) {
			globalLogout(err)
			setError('Error fetching notifications');
			console.error('Error:', err);
		} finally {
			setLoading(false);
		}
	};

	const markAsRead = async (id) => {
		try {
			const token = Cookies.get("ecom_user_token");

			// Update the local state first for immediate feedback
			setNotifications(prev => prev.map(notif =>
				notif._id === id ? { ...notif, read: true } : notif
			));

			// Then make the API call
			await axios.patch(`/api/notification/markAsRead/${id}`, {}, {
				headers: {
					'Authorization': `Bearer ${token}`
				}
			});
		} catch (err) {
			console.error('Error marking notification as read:', err);
			// Revert the change if the API call fails
			setNotifications(prev => prev.map(notif =>
				notif._id === id ? { ...notif, read: false } : notif
			));
		}
	};

	const markAllAsRead = async () => {
		try {
			const token = Cookies.get("ecom_user_token");

			// Update the local state first for immediate feedback
			setNotifications(prev => prev.map(notif => ({ ...notif, read: true })));

			// Then make the API call
			await axios.patch('/api/notification/markAllAsRead', {}, {
				headers: {
					'Authorization': `Bearer ${token}`
				}
			});
		} catch (err) {
			console.error('Error marking all notifications as read:', err);
			// Revert the change if the API call fails
			setNotifications(prev => prev.map(notif => ({ ...notif, read: false })));
		}
	};

	const formatDate = (dateString) => {
		const date = new Date(dateString);
		const now = new Date();
		const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

		if (diffInHours < 1) {
			const diffInMinutes = Math.floor((now - date) / (1000 * 60));
			if (diffInMinutes < 1) return 'Just now';
			return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
		} else if (diffInHours < 24) {
			return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
		} else {
			const diffInDays = Math.floor(diffInHours / 24);
			if (diffInDays < 7) {
				return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
			} else {
				return date.toLocaleDateString();
			}
		}
	};

	const getNotificationIcon = (type) => {
		switch (type) {
			case 'ADMIN_REQUEST_RECEIVED':
				return (
					<div className="p-2 bg-blue-100 rounded-full">
						<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
						</svg>
					</div>
				);
			default:
				return (
					<div className="p-2 bg-gray-100 rounded-full">
						<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
						</svg>
					</div>
				);
		}
	};

	if (loading) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<div className="bg-white p-6 rounded-lg shadow-md text-center">
					<svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-red-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
					</svg>
					<p className="text-gray-700 mb-4">{error}</p>
					<button
						onClick={fetchNotifications}
						className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
					>
						Try Again
					</button>
				</div>
			</div>
		);
	}

	const unreadCount = notifications.filter(notif => !notif.read).length;

	return (
		<><Header />
			<div className="min-h-screen bg-gray-50 py-8">
				<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="bg-white rounded-xl shadow-md overflow-hidden">
						<div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
							<h2 className="text-xl font-semibold text-gray-800">Notifications</h2>
							<div className="flex items-center space-x-4">
								{/* {unreadCount > 0 && (
                <button 
                  onClick={markAllAsRead}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Mark all as read
                </button>
              )} */}
								<span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
									{notifications.length} {notifications.length === 1 ? 'notification' : 'notifications'}
								</span>
							</div>
						</div>

						<div className="divide-y divide-gray-200">
							{notifications.length === 0 ? (
								<div className="text-center py-12">
									<svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
									</svg>
									<p className="text-gray-500">No notifications yet</p>
								</div>
							) : (
								notifications.map(notification => (
									<div
										key={notification._id}
										className={`p-6 flex space-x-4 transition-colors duration-200 ${!notification.read ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
									>
										<div className="flex-shrink-0">
											{getNotificationIcon(notification.type)}
										</div>
										<div className="flex-1 min-w-0">
											<div className="flex justify-between items-start">
												<p className={`text-sm font-medium ${!notification.read ? 'text-blue-800' : 'text-gray-900'}`}>
													{notification.title}
												</p>
												<span className="text-xs text-gray-500 whitespace-nowrap">
													{formatDate(notification.createdAt)}
												</span>
											</div>
											<p className="text-sm text-gray-600 mt-1">
												{notification.message}
											</p>
											{notification.data && (
												<div className="mt-2 text-xs text-gray-500">
													{notification.data.requestType && (
														<span className="inline-block bg-gray-100 rounded-full px-2 py-1 mr-2">
															Type: {notification.data.requestType}
														</span>
													)}
													{notification.data.sellerName && (
														<span className="inline-block bg-gray-100 rounded-full px-2 py-1">
															Seller: {notification.data.sellerName}
														</span>
													)}
													{notification.data.sellerId && !notification.data.sellerName && (
														<span className="inline-block bg-gray-100 rounded-full px-2 py-1">
															Seller ID: {notification.data.sellerId}
														</span>
													)}
												</div>
											)}
										</div>
										{/* {!notification.read && (
                    <button 
                      onClick={() => markAsRead(notification._id)}
                      className="flex-shrink-0 text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Mark as read
                    </button>
                  )} */}
									</div>
								))
							)}
						</div>
					</div>
				</div>
			</div>
			<Footer />
		</>
	);
};

export default NotificationsPage;